import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Alert } from '@/components/Alert'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { NoteThread } from '@/components/NoteThread'
import { MCancelrestore } from '@/screens/m-cancelrestore/MCancelrestore'
import { JOB_STATUS_CHIP, RESTORE_RESULT_CHIP } from '@/screens/shared/jobFormatting'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId, setActiveWorkspace } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestoreJob = components['schemas']['RestoreJob']
type RestoreRepositoryProgress = components['schemas']['RestoreRepositoryProgress']
type RestoreObjectRow = components['schemas']['RestoreObjectRow']
type RestoreObjectResult = components['schemas']['RestoreObjectResult']
type LogPage = components['schemas']['LogPage']
type JobNote = components['schemas']['JobNote']

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  PARTIAL_SUCCESS: 'Partial success',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
}

const RESULT_LABEL: Record<string, string> = { WRITTEN: 'Written', NEEDS_INPUT: 'Needs you', SKIPPED: 'Skipped', FAILED: 'Failed', REFUSED: 'Refused', MIXED: 'Mixed' }

const KIND_LABEL: Record<string, string> = {
  REPOSITORY: 'Repository',
  REF: 'Ref',
  SETTINGS_POLICY: 'Settings & policy',
  ACCESS_INTEGRATION: 'Access & integrations',
  CI_DEPLOYMENT: 'CI & deployment',
  REVIEW_STATE: 'Pull request state',
  PROJECT_POLICY: 'Project policy',
  WORKSPACE_WEBHOOK: 'Workspace webhooks',
}

type Tab = 'summary' | 'repositories' | 'objects' | 'notes' | 'log'

/**
 * `v-restorejob` — process/09-SCREEN-SPECS/v-restorejob.md. `by_object_type`/`outcome_counts` are
 * server aggregates (rule/§24.2 rule 24) — this screen folds no page itself. `REFUSED`/`SKIPPED`
 * rows never get the failure treatment (rule 3) — rendered as a plain chip, not an alert.
 * `components_not_written` is named by component, not padded with a count. Two restore kinds
 * (`PROJECT_POLICY`, `WORKSPACE_WEBHOOK`) legitimately have zero rows on the Repositories tab —
 * that is not an empty-state defect.
 *
 * **`panel`/`panel-header`/`meta-block`/`kv-key`/`kv-value`/`avatar`/`link`/`grid-tools`/
 * `icon-button`/`tools` added W165.** A real `meta-block` replaced the flat header row: full
 * `job_id`, kind, `triggered_by` (with a real `Avatar`, never invented for a null trigger — this
 * job's own field is optional even though the operation's own description says a restore is
 * "always a real person"), started/completed/duration, the restore point's own handle, and the
 * destination workspace as a real `link` — switching the active workspace, the same
 * `setActiveWorkspace` pattern `v-dashboard`'s own workspace table already uses — when it differs
 * from the one already active. Each tab gained a real "Refresh" (`grid-tools`, scoped to that tab's
 * own query) and the tab bar itself gained a page-level one (`tools`), matching `v-backupjob`'s own
 * fix (W164) and the prototype's own placement.
 *
 * **This spec's own `segmented-control`/`segment-option` (2×, "in `pager` · `segmented-control`")
 * is not built, and the `pager` half of that description is a real conflict with an already-settled
 * rule, not an oversight.** A segmented, numbered pager (page-number buttons, the prototype's own
 * style here) directly contradicts §32.8/rule 25 — "the footer carries no total, no page number;
 * paging is cursor-only" — already enforced on every paged list in this build. Building it would
 * mean reversing a rule applied everywhere else for one screen.
 *
 * **`grid-filter-row` closed for the Objects tab only, using a real parameter — `restoreListJobRepositories`
 * itself still has none.** `restoreListJobObjects` takes a real `result` filter (`RestoreObjectResult`,
 * the same enum `RESULT_LABEL` already names) — wired as a real dropdown, resetting the cursor on
 * change. Its own `repository_id` parameter ("for the expandable per-repository view") would need a
 * cross-tab drill-down from the Repositories tab this pass does not build; left for later.
 *
 * **Every status/result chip on this screen now uses `JOB_STATUS_CHIP`/`RESTORE_RESULT_CHIP`
 * (W172)** — the job status, the per-repository progress status, the outcome-count summary chips
 * and both per-row result chips were all `variant="plain"` (or a blue/grey binary for the top job
 * status), regardless of the real outcome. See `jobFormatting.ts`'s own doc comment for the
 * prototype evidence behind each mapping.
 *
 * **`notes` tab added** — was a flagged contract gap (`restoreListNotes`/`restoreAddNote` did not
 * exist), closed at the source rather than built against a fabricated shape; see `NoteThread.tsx`'s
 * own doc comment for the shared component both job screens draw this from.
 *
 * **`PERMISSION_RAISED_ABOVE_PRIOR` given its own warning chip on the Objects tab's Reason
 * column** — previously rendered as the same plain enum text as every other reason code, sitting
 * directly under a green `Written`/`Updated` result chip with nothing to separate it from an
 * ordinary successful write. This is §18.11/`OI-C23`'s one detective control for the whole restore
 * path — the compensating control for taking per-object re-authentication off it — so an operator
 * scanning a long report for exactly this row needs it to read as distinct at a glance, not as
 * text they have to read in full to notice.
 *
 * **Log tab rebuilt from page-replacing to accumulating (Sanket, 2026-09-25, found live)** — mirrors
 * `VBackupjob.tsx`'s own fix, same bug: a Prev/Next `Pager` over `cursor`-only pages threw away the
 * lines already on screen on every "Next" click (no `prev_cursor` exists to get them back, §24.2
 * rule 25), which reads as "the log only has half of what I expect" the first time anyone pages
 * forward. Replaced with a running transcript plus a "Load more" action that only ever appends.
 */
export function VRestorejob() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const [tab, setTab] = useState<Tab>('summary')
  const [cancelling, setCancelling] = useState(false)
  const [objectResult, setObjectResult] = useState<RestoreObjectResult | ''>('')
  const [morePages, setMorePages] = useState<readonly LogPage[]>([])

  const job = useQuery({
    queryKey: ['restore-job', orgId, workspaceId, jobId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<RestoreJob> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RestoreJob
    },
  })

  const repositories = useQuery({
    queryKey: ['restore-job-repos', orgId, workspaceId, jobId, pager.cursor],
    enabled: tab === 'repositories',
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/repositories', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: pager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const objects = useQuery({
    queryKey: ['restore-job-objects', orgId, workspaceId, jobId, pager.cursor, objectResult],
    enabled: tab === 'objects',
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/objects', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: pager.cursor, result: objectResult || undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const logs = useQuery({
    queryKey: ['restore-job-logs', orgId, workspaceId, jobId],
    enabled: tab === 'log',
    queryFn: async (): Promise<LogPage> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/logs', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as LogPage
    },
  })

  // Accumulating, not paged-and-replaced — mirrors `VBackupjob.tsx`'s own fix (found live,
  // 2026-09-25): "Load more" only ever appends, so nothing already shown here is lost by paging on.
  const logLines = [...(logs.data?.lines ?? []), ...morePages.flatMap((p) => p.lines)]
  const logNextCursor = morePages.length > 0 ? morePages[morePages.length - 1]!.next_cursor : logs.data?.next_cursor
  const loadMoreLog = useMutation({
    mutationFn: async (): Promise<LogPage> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/logs', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: logNextCursor ?? undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as LogPage
    },
    onSuccess: (page) => setMorePages((prev) => [...prev, page]),
  })

  const notes = useQuery({
    queryKey: ['restore-job-notes', orgId, workspaceId, jobId],
    enabled: tab === 'notes',
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/notes', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return (data?.items ?? []) as unknown as readonly JobNote[]
    },
  })

  const addNote = useMutation({
    mutationFn: async (body: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/notes', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
        body: { body },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['restore-job-notes', orgId, workspaceId, jobId] }),
  })

  async function downloadBlob(path: '/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/objects/export' | '/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/logs/export', filename: string) {
    const client = getApiClient()
    const response = await client.GET(path, { params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } }, parseAs: 'blob' })
    if (response.error) throw new ApiProblemError(response.error as Problem)
    const blob = response.data as unknown as Blob
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportObjects = useMutation({ mutationFn: () => downloadBlob('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/objects/export', `restore-${jobId}-objects.csv`) })
  const exportLogs = useMutation({ mutationFn: () => downloadBlob('/orgs/{org_id}/workspaces/{workspace_id}/restores/{job_id}/logs/export', `restore-${jobId}-log.csv`) })

  const jobProblem = job.error instanceof ApiProblemError ? job.error.problem : undefined
  const data = job.data

  return (
    <div data-scr={unitId('v-restorejob')}>
      {jobProblem && (isUnexpectedRefusal(jobProblem.refusal_kind) ? <UnexpectedRefusal problem={jobProblem} /> : <Note>{jobProblem.title}</Note>)}

      {data && (
        <>
          <div className="flex items-center gap-6 mb-6">
            <h1 className="m-0 text-section font-semibold">
              Restore <span className="font-mono">{data.job.handle}</span>
            </h1>
            <Chip variant={JOB_STATUS_CHIP[data.job.status] ?? 'plain'}>{STATUS_LABEL[data.job.status] ?? data.job.status}</Chip>
            {data.job.status === 'RUNNING' && !data.cancellation && (
              <Button data-act={undefined} ghost onClick={() => setCancelling(true)}>
                Cancel
              </Button>
            )}
            {data.cancellation && <Note>Cancelling…</Note>}
          </div>

          {data.secured_values_outstanding && data.secured_values_outstanding.numerator > 0 && (
            <div className="mb-6">
              <Alert variant="warn">
                {data.secured_values_outstanding.caption} still to enter — this restore is terminally partial until they are.{' '}
                <button
                  type="button"
                  data-act={actionId('nav.v-pending')}
                  className="underline bg-transparent border-0 p-0 cursor-pointer font-semibold"
                  onClick={() => navigate(`/pending?job_id=${jobId}`)}
                >
                  Enter them
                </button>
              </Alert>
            </div>
          )}

          {data.components_not_written && data.components_not_written.length > 0 && (
            <div className="mb-6">
              <Alert variant="info">Not written: {data.components_not_written.join(', ')}</Alert>
            </div>
          )}

          <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
            <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">
              Job details
            </div>
            <div data-cmp="meta-block" className="grid gap-4 p-5" style={{ gridTemplateColumns: '200px 1fr' }}>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Job ID
              </div>
              <div data-cmp="kv-value" className="font-mono text-body-lg">{data.job.job_id}</div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Type
              </div>
              <div data-cmp="kv-value" className="text-body-lg">
                <Chip variant="plain">{KIND_LABEL[data.restore_kind] ?? data.restore_kind}</Chip>
              </div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Initiator
              </div>
              <div data-cmp="kv-value" className="text-body-lg">
                {data.triggered_by?.display_name ? (
                  <>
                    <Avatar name={data.triggered_by.display_name} />
                    {data.triggered_by.display_name}
                  </>
                ) : (
                  'System'
                )}
              </div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Start time
              </div>
              <div data-cmp="kv-value" className="text-body-lg">{data.started_at ? data.started_at.display : '—'}</div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                End time
              </div>
              <div data-cmp="kv-value" className="text-body-lg">{data.completed_at ? data.completed_at.display : '—'}</div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Time taken
              </div>
              <div data-cmp="kv-value" className="text-body-lg">{data.duration ? data.duration.display : '—'}</div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Restore point
              </div>
              <div data-cmp="kv-value" className="font-mono text-body-lg">{data.restore_point.data_snapshot.handle}</div>
              <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                Destination workspace
              </div>
              <div data-cmp="kv-value" className="text-body-lg">
                {data.destination.workspace_id === workspaceId ? (
                  data.destination.workspace_name
                ) : (
                  <button
                    type="button"
                    data-cmp="link"
                    className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-body-lg"
                    onClick={() => {
                      setActiveWorkspace(data.destination.workspace_id)
                      navigate('/dashboard')
                    }}
                    title="Make this the active workspace"
                  >
                    {data.destination.workspace_name}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div data-cmp="tabs" className="flex items-center gap-4 border-b border-bd mb-6">
            {(['summary', 'repositories', 'objects', 'notes', 'log'] as const).map((t) => (
              <button
                key={t}
                type="button"
                data-cmp="tab"
                onClick={() => {
                  pager.reset()
                  setMorePages([])
                  setTab(t)
                }}
                className={`py-3 px-4 text-body-lg font-semibold border-b-2 ${tab === t ? 'border-blue text-tx' : 'border-transparent text-muted'}`}
              >
                {t === 'summary' ? 'Summary' : t === 'repositories' ? 'Repositories' : t === 'objects' ? 'Objects' : t === 'notes' ? 'Job notes' : 'Log'}
              </button>
            ))}
            <div data-cmp="tools" className="ml-auto pb-2">
              <IconButton
                data-act={undefined}
                icon="i-refresh"
                label="Refresh"
                onClick={() => {
                  if (tab === 'repositories') queryClient.invalidateQueries({ queryKey: ['restore-job-repos', orgId, workspaceId, jobId] })
                  else if (tab === 'objects') queryClient.invalidateQueries({ queryKey: ['restore-job-objects', orgId, workspaceId, jobId] })
                  else if (tab === 'notes') queryClient.invalidateQueries({ queryKey: ['restore-job-notes', orgId, workspaceId, jobId] })
                  else if (tab === 'log') {
                    queryClient.invalidateQueries({ queryKey: ['restore-job-logs', orgId, workspaceId, jobId] })
                    setMorePages([])
                  } else queryClient.invalidateQueries({ queryKey: ['restore-job', orgId, workspaceId, jobId] })
                }}
              />
            </div>
          </div>

          {tab === 'summary' && (
            <div data-cmp="pane">
              {data.outcome_counts && (
                <div className="flex flex-wrap gap-3 mb-7">
                  <Chip variant={RESTORE_RESULT_CHIP.WRITTEN}>{data.outcome_counts.written ?? 0} written</Chip>
                  <Chip variant={RESTORE_RESULT_CHIP.NEEDS_INPUT}>{data.outcome_counts.needs_input ?? 0} needs input</Chip>
                  <Chip variant={RESTORE_RESULT_CHIP.SKIPPED}>{data.outcome_counts.skipped ?? 0} skipped</Chip>
                  <Chip variant={RESTORE_RESULT_CHIP.FAILED}>{data.outcome_counts.failed ?? 0} failed</Chip>
                  <Chip variant={RESTORE_RESULT_CHIP.REFUSED}>{data.outcome_counts.refused ?? 0} refused</Chip>
                </div>
              )}
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">By object type</span>
                  <div data-cmp="grid-tools">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={job.isFetching}
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['restore-job', orgId, workspaceId, jobId] })}
                    />
                  </div>
                </div>
                <GridTable
                  columns={[
                    { key: 'objectType', header: 'Object type', render: (row) => row.object_type },
                    { key: 'action', header: 'Action', render: (row) => row.action },
                    { key: 'result', header: 'Result', render: (row) => <Chip variant={RESTORE_RESULT_CHIP[row.result] ?? 'plain'}>{RESULT_LABEL[row.result] ?? row.result}</Chip> },
                    { key: 'attempted', header: 'Attempted', render: (row) => row.attempted },
                    { key: 'written', header: 'Written', render: (row) => row.written },
                  ]}
                  rows={data.by_object_type ?? []}
                  getRowKey={(row) => row.object_type}
                />
              </div>
            </div>
          )}

          {tab === 'repositories' && (
            <div data-cmp="pane">
              <Note>Project-policy and workspace-webhook restores legitimately have no rows here — their progress is on the Objects tab instead.</Note>
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-4">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Repositories</span>
                  <div data-cmp="grid-tools">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={repositories.isFetching}
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['restore-job-repos', orgId, workspaceId, jobId] })}
                    />
                  </div>
                </div>
                <GridTable
                  columns={[
                    { key: 'repository', header: 'Repository', render: (r) => r.slug },
                    { key: 'status', header: 'Status', render: (r) => <Chip variant={JOB_STATUS_CHIP[r.status] ?? 'plain'}>{STATUS_LABEL[r.status] ?? r.status}</Chip> },
                    { key: 'written', header: 'Written', render: (r) => r.objects_written ?? '—' },
                    { key: 'skipped', header: 'Skipped', render: (r) => r.objects_skipped ?? '—' },
                  ]}
                  rows={(repositories.data?.items ?? []) as unknown as readonly RestoreRepositoryProgress[]}
                  getRowKey={(r) => r.repository_id}
                />
                <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                  <Note>Paged by cursor only.</Note>
                  <Pager
                    hasPrev={pager.hasPrev}
                    hasNext={Boolean(repositories.data?.next_cursor)}
                    loading={repositories.isFetching}
                    onPrev={pager.goPrev}
                    onNext={() => {
                      if (repositories.data?.next_cursor) pager.goNext(repositories.data.next_cursor)
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'objects' && (
            <div data-cmp="pane">
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Objects</span>
                  <div data-cmp="grid-tools" className="flex items-center gap-4">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={objects.isFetching}
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['restore-job-objects', orgId, workspaceId, jobId] })}
                    />
                    <Button data-act={actionId('restoreObjects.export')} ghost disabled={exportObjects.isPending} onClick={() => exportObjects.mutate()}>
                      Export objects
                    </Button>
                  </div>
                </div>
                <div data-cmp="grid-filter-row" className="p-5 border-b border-bd">
                  <select
                    value={objectResult}
                    onChange={(e) => {
                      pager.reset()
                      setObjectResult(e.target.value as RestoreObjectResult | '')
                    }}
                    className="border border-bd2 rounded-1 py-2 px-3 text-small"
                  >
                    <option value="">All results</option>
                    {(['WRITTEN', 'NEEDS_INPUT', 'SKIPPED', 'FAILED', 'REFUSED'] as const).map((r) => (
                      <option key={r} value={r}>
                        {RESULT_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </div>
                <GridTable
                  columns={[
                    { key: 'object', header: 'Object', render: (item) => item.row.object_label },
                    { key: 'repository', header: 'Repository', render: (item) => item.row.repository?.slug ?? '—' },
                    { key: 'action', header: 'Action', render: (item) => item.row.action },
                    {
                      key: 'result',
                      header: 'Result',
                      render: (item) => <Chip variant={RESTORE_RESULT_CHIP[item.row.result] ?? 'plain'}>{RESULT_LABEL[item.row.result] ?? item.row.result}</Chip>,
                    },
                    {
                      key: 'reason',
                      header: 'Reason',
                      render: (item) => (
                        <>
                          {item.row.reason === 'PERMISSION_RAISED_ABOVE_PRIOR' ? (
                            <Chip variant="warning">Permission raised above prior</Chip>
                          ) : (
                            item.row.reason
                          )}
                          {item.row.reason_detail && <Note>{item.row.reason_detail}</Note>}
                        </>
                      ),
                    },
                  ]}
                  rows={((objects.data?.items ?? []) as unknown as readonly RestoreObjectRow[]).map((row, i) => ({ row, i }))}
                  getRowKey={(item) => String(item.i)}
                />
                <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                  <Note>Paged by cursor only — row order is write order, never re-sorted.</Note>
                  <Pager
                    hasPrev={pager.hasPrev}
                    hasNext={Boolean(objects.data?.next_cursor)}
                    loading={objects.isFetching}
                    onPrev={pager.goPrev}
                    onNext={() => {
                      if (objects.data?.next_cursor) pager.goNext(objects.data.next_cursor)
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'notes' && (
            <div data-cmp="pane">
              <NoteThread notes={notes.data ?? []} pending={addNote.isPending} onAdd={(body) => addNote.mutate(body)} addActionId={actionId('restore.note.add')} />
            </div>
          )}

          {tab === 'log' && (
            <div data-cmp="pane">
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Log</span>
                  <div data-cmp="grid-tools" className="flex items-center gap-4">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={logs.isFetching}
                      onClick={() => {
                        queryClient.invalidateQueries({ queryKey: ['restore-job-logs', orgId, workspaceId, jobId] })
                        setMorePages([])
                      }}
                    />
                    <Button data-act={actionId('restoreLogs.export')} ghost disabled={logs.data?.retention_expired || exportLogs.isPending} onClick={() => exportLogs.mutate()} title={logs.data?.retention_expired ? 'Logs expired' : undefined}>
                      Export logs
                    </Button>
                  </div>
                </div>
                {logs.data?.retention_expired ? (
                  <Note>Logs expired. The log group's retention boundary has passed for this job.</Note>
                ) : (
                  <>
                    <div className="font-mono text-body flex flex-col gap-2 p-5">
                      {logLines.map((line, i) => (
                        <div key={i} className={line.level === 'ERROR' ? 'text-fail' : line.level === 'WARNING' ? 'text-warn' : 'text-tx2'}>
                          [{line.level}] {line.at} — {line.message}
                        </div>
                      ))}
                      {logLines.length === 0 && !logs.isFetching && <Note>This job has not logged anything yet.</Note>}
                    </div>
                    <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                      <Note>
                        {logLines.length} line{logLines.length === 1 ? '' : 's'} loaded so far — logs only ever load forward, so
                        nothing already shown here goes away.
                      </Note>
                      {logNextCursor && (
                        <Button data-act={undefined} ghost disabled={loadMoreLog.isPending} onClick={() => loadMoreLog.mutate()}>
                          {loadMoreLog.isPending ? 'Loading…' : 'Load more'}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {cancelling && (
            <MCancelrestore
              orgId={orgId}
              workspaceId={workspaceId}
              job={data.job}
              restoreKindLabel={KIND_LABEL[data.restore_kind] ?? data.restore_kind}
              onDismiss={() => setCancelling(false)}
              onCancelled={() => {
                setCancelling(false)
                queryClient.invalidateQueries({ queryKey: ['restore-job', orgId, workspaceId, jobId] })
              }}
            />
          )}
        </>
      )}
    </div>
  )
}
